// src/redux/companiesSlice.ts
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  collection, doc, setDoc, getDocs,
  deleteDoc, query, orderBy, Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";

/* ─── Types ─── */
export interface Company {
  id:          string;
  name:        string;
  slug:        string;
  tagline:     string;
  description: string;
  industry:    string;
  website:     string;
  founded:     string;
  location:    string;
  logoUrl:     string;
  coverImage:  string;
  mediaUrls:   string[];     // gallery / extra images
  published:   boolean;
  createdAt:   any;
  updatedAt:   any;
}

/* ─────────────────────────────────────────
   THUNKS
───────────────────────────────────────── */

/* UPSERT COMPANY */
export const upsertCompany = createAsyncThunk(
  "companies/upsertCompany",
  async (payload: Partial<Company> & { name: string; slug: string }) => {
    const id  = payload.id || payload.slug;
    const ref = doc(db, "companies", id);
    const data = {
      name:        payload.name,
      slug:        payload.slug,
      tagline:     payload.tagline     || "",
      description: payload.description || "",
      industry:    payload.industry    || "",
      website:     payload.website     || "",
      founded:     payload.founded     || "",
      location:    payload.location    || "",
      logoUrl:     payload.logoUrl     || "",
      coverImage:  payload.coverImage  || "",
      mediaUrls:   payload.mediaUrls   || [],
      published:   payload.published   ?? false,
      updatedAt:   Timestamp.now(),
      createdAt:   Timestamp.now(),
    };
    await setDoc(ref, data, { merge: true });
    return { id, ...data };
  }
);

/* UPSERT COMPANY MEDIA */
export const upsertCompanyMedia = createAsyncThunk(
  "companies/upsertCompanyMedia",
  async ({ id, mediaUrls }: { id: string; mediaUrls: string[] }) => {
    const ref = doc(db, "companies", id);
    await setDoc(ref, { mediaUrls, updatedAt: Timestamp.now() }, { merge: true });
    return { id, mediaUrls };
  }
);

/* FETCH ALL COMPANIES */
export const fetchCompanies = createAsyncThunk(
  "companies/fetchCompanies",
  async () => {
    const q        = query(collection(db, "companies"), orderBy("createdAt", "desc"));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() })) as Company[];
  }
);

/* DELETE COMPANY */
export const deleteCompany = createAsyncThunk(
  "companies/deleteCompany",
  async (id: string) => {
    await deleteDoc(doc(db, "companies", id));
    return id;
  }
);

/* ─────────────────────────────────────────
   SLICE
───────────────────────────────────────── */
const companiesSlice = createSlice({
  name: "companies",
  initialState: {
    companies: [] as Company[],
    status:    "idle" as "idle" | "loading" | "failed",
    error:     null as string | null,
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchCompanies.pending,   state => { state.status = "loading"; })
      .addCase(fetchCompanies.fulfilled, (state, action) => {
        state.status    = "idle";
        state.companies = action.payload;
      })
      .addCase(fetchCompanies.rejected, (state, action) => {
        state.status = "failed";
        state.error  = action.error.message || "Failed to fetch companies";
      })
      .addCase(upsertCompany.fulfilled, (state, action) => {
        const idx = state.companies.findIndex(c => c.id === action.payload.id);
        if (idx >= 0) state.companies[idx] = action.payload as Company;
        else          state.companies.unshift(action.payload as Company);
      })
      .addCase(upsertCompanyMedia.fulfilled, (state, action) => {
        const idx = state.companies.findIndex(c => c.id === action.payload.id);
        if (idx >= 0) state.companies[idx].mediaUrls = action.payload.mediaUrls;
      })
      .addCase(deleteCompany.fulfilled, (state, action) => {
        state.companies = state.companies.filter(c => c.id !== action.payload);
      });
  },
});

export default companiesSlice.reducer;